import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { assetService } from "../services/assetService";
import { systemService } from "../services/systemService";
import {
  transactionService,
} from "../services/transactionService";
import { partnerService } from "../services/partnerService";
import { partnerSatService } from "../services/partnerSatService";
import { ALive, partnerAliveService } from "../services/partnerAliveService";
import websocketService from "../services/websocketService";
import {
  Interpreter,
  partnerInterpreterService,
} from "../services/partnerInterpreterService";
import { loggingService } from "../services/logService";
import { getPageConfigField } from "../utils/getConfigs";

interface CallStateMessage {
  type: "call_state";
  statusCode?: number | string;
  statusKey?: string;
  statusPhase?: string;
  patientJoinUrl?: string;
  insuranceConsentRequested?: boolean;
  isAssignedToTyto?: boolean;
}

export function useCallLogic() {
  const RECONNECT_INTERVAL_MS = 2000;

  const pageKey = "call";
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  const [logo, setLogo] = useState<string>();
  const [loading, setLoading] = useState<string>();
  const [recipientImage, setRecipientImage] = useState<string>();
  const [recipientName, setRecipientName] = useState<string>();
  const [recipientSpecialty, setRecipientSpecialty] = useState<string>();
  const [transactionStatus, setTransactionStatus] = useState<number>(0);
  const [isCancelDisabled, setIsCancelDisabled] = useState(false);
  const [acsJoinUrl, setAcsJoinUrl] = useState<string | null>(null);
  const [isAcsIframeActive, setIsAcsIframeActive] = useState(false);
  const [isConsentModalOpened, setIsConsentModalOpened] = useState(false);

  const [isTytoModalOpened, setIsTytoModalOpened] = useState(false);
  const [isTytoModalDismissed, setIsTytoModalDismissed] = useState(false);

  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const partner = partnerService.getPartner();
  const hasCreatedQueue = useRef(false);
  const isIntentionalLeave = useRef(false);
  const isWebSocketConnectedRef = useRef(false);
  const lastCallStateStatusCode = useRef<number | null>(null);
  const pendingJoinUrl = useRef<string | null>(null);
  const isSessionCoverageActiveRef = useRef(false);
  const sessionTransactionCodeRef = useRef<string | null>(null);
  const sessionBusinessUnitRef = useRef<string | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isReconnectInFlightRef = useRef(false);
  const runReconnectAttemptRef = useRef<(transactionCode: string) => void>(
    () => {}
  );
  const callStateJoinUrlTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const hasProcessedCallEnd = useRef(false);

  const recipientNameRef = useRef<string>();
  const recipientSpecialtyRef = useRef<string>();
  useEffect(() => {
    recipientNameRef.current = recipientName;
    recipientSpecialtyRef.current = recipientSpecialty;
  }, [recipientName, recipientSpecialty]);

  useEffect(() => {
    isWebSocketConnectedRef.current = isWebSocketConnected;
  }, [isWebSocketConnected]);

  const isShowMTLBenefitModal = getPageConfigField(
    pageKey,
    "isShowMTLBenefitModal"
  );
  const [isMTLBenefitModalOpened, setIsMTLBenefitModalOpened] = useState(
    isShowMTLBenefitModal
  );

  const deactivateAcsIframe = useCallback(() => {
    if (callStateJoinUrlTimer.current) {
      clearTimeout(callStateJoinUrlTimer.current);
      callStateJoinUrlTimer.current = null;
    }
    pendingJoinUrl.current = null;
    setIsAcsIframeActive(false);
  }, []);

  const queueAcsJoinUrl = useCallback(
    (joinUrl: string) => {
      if (!joinUrl) return;

      const isSameJoinUrl = acsJoinUrl === joinUrl;
      if (
        pendingJoinUrl.current === joinUrl ||
        (isSameJoinUrl && isAcsIframeActive)
      ) {
        return;
      }

      pendingJoinUrl.current = joinUrl;

      if (callStateJoinUrlTimer.current) {
        clearTimeout(callStateJoinUrlTimer.current);
      }

      console.log("[WebSocket] Call state join URL received:", joinUrl);

      loggingService(
        "join_call",
        {
          message: "[WebSocket] Call state join URL received",
          data: { joinUrl },
        },
        "[WebSocket] Logging join_call failed"
      );

      callStateJoinUrlTimer.current = setTimeout(() => {
        if (pendingJoinUrl.current !== joinUrl) {
          return;
        }

        setAcsJoinUrl(joinUrl);
        setIsAcsIframeActive(true);
        pendingJoinUrl.current = null;
        callStateJoinUrlTimer.current = null;

        console.log(
          `[WebSocket] Initializing iframe after 5s delay for ${recipientNameRef.current || "callee"}`
        );
      }, 5000);
    },
    [acsJoinUrl, isAcsIframeActive]
  );

  const resolveCallStatePhase = useCallback(
    (statusCode: number, statusPhase?: string): string => {
      if (statusPhase) return statusPhase;
      if (Number.isNaN(statusCode)) return "unknown";
      if ([0, 1, 3, 5].includes(statusCode)) return "waiting";
      if ([2, 4, 6].includes(statusCode)) return "in_call";
      if ([7, 8, 9, 10, 11].includes(statusCode)) return "ended";
      if (statusCode === 100) return "special";
      return "unknown";
    },
    []
  );

  const checkQueueRef = useRef<() => Promise<void>>(async () => {});

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const resetQueueUiState = useCallback(() => {
    if (callStateJoinUrlTimer.current) {
      clearTimeout(callStateJoinUrlTimer.current);
      callStateJoinUrlTimer.current = null;
    }

    pendingJoinUrl.current = null;
    lastCallStateStatusCode.current = null;
    setAcsJoinUrl(null);
    setIsAcsIframeActive(false);
    setTransactionStatus(0);
    setIsCancelDisabled(false);
    setRecipientImage(undefined);
    setRecipientName(undefined);
    setRecipientSpecialty(undefined);
    setIsConsentModalOpened(false);
    setIsTytoModalOpened(false);
    setIsTytoModalDismissed(false);
  }, []);

  const stopSessionCoverage = useCallback(() => {
    isSessionCoverageActiveRef.current = false;
    sessionTransactionCodeRef.current = null;
    sessionBusinessUnitRef.current = null;
    isReconnectInFlightRef.current = false;
    clearReconnectTimer();
  }, [clearReconnectTimer]);

  const startSessionCoverage = useCallback(
    (transactionCode: string, businessUnit: string) => {
      hasProcessedCallEnd.current = false;
      isSessionCoverageActiveRef.current = true;
      sessionTransactionCodeRef.current = transactionCode;
      sessionBusinessUnitRef.current = businessUnit;
      isReconnectInFlightRef.current = false;
      clearReconnectTimer();
    },
    [clearReconnectTimer]
  );

  const resetActiveWebSocketConnection = useCallback(() => {
    clearReconnectTimer();
    isReconnectInFlightRef.current = false;
    websocketService.disconnect();
    isWebSocketConnectedRef.current = false;
    setIsWebSocketConnected(false);
  }, [clearReconnectTimer]);

  const finishSessionAndNavigateToReview = useCallback(() => {
    if (hasProcessedCallEnd.current) {
      return;
    }

    hasProcessedCallEnd.current = true;
    stopSessionCoverage();
    resetActiveWebSocketConnection();
    transactionService.clearJoinUrl();
    resetQueueUiState();
    isIntentionalLeave.current = true;
    navigate("/review");
  }, [
    navigate,
    resetActiveWebSocketConnection,
    resetQueueUiState,
    stopSessionCoverage,
  ]);

  const handleConsentRequest = useCallback(async (data: any) => {
    console.log("[WebSocket] Processing consent request:", data);
    setIsConsentModalOpened(true);
  }, []);

  const handleConsentResponse = useCallback(async (data: any) => {
    console.log("[WebSocket] Processing consent response:", data);
    setIsConsentModalOpened(false);
  }, []);

  const handleTytoAssignment = useCallback(async (data: any) => {
    console.log("[WebSocket] Processing Tyto assignment:", data);
    if (!isTytoModalOpened && !isTytoModalDismissed) {
      setIsTytoModalOpened(true);
    }
  }, [isTytoModalDismissed, isTytoModalOpened]);

  const handleTytoSmsStatus = useCallback(async (data: any) => {
    console.log("[WebSocket] Processing Tyto SMS status:", data);
  }, []);

  const handleCallEnded = useCallback(
    async (data: any) => {
      if (hasProcessedCallEnd.current) return;

      console.log("[WebSocket] Processing final call completion:", data);
      await loggingService(
        "call_ended",
        { message: "[WebSocket] Processing final call completion", data },
        "[WebSocket] Logging call_ended failed"
      );

      finishSessionAndNavigateToReview();
    },
    [finishSessionAndNavigateToReview]
  );

  const handleCallTransferred = useCallback(
    async (data: any) => {
      console.log("[WebSocket] Processing call transfer:", data);
      await loggingService(
        "call_transferred",
        { message: "[WebSocket] Processing call transfer", data },
        "[WebSocket] Logging call_transferred failed"
      );

      deactivateAcsIframe();
      await checkQueueRef.current();
    },
    [deactivateAcsIframe]
  );

  const handleCallState = useCallback(
    async (callState: CallStateMessage) => {
      const statusCode = Number(callState.statusCode);
      const resolvedPhase = resolveCallStatePhase(statusCode, callState.statusPhase);

      if (!Number.isNaN(statusCode) && lastCallStateStatusCode.current !== statusCode) {
        lastCallStateStatusCode.current = statusCode;
        setTransactionStatus(statusCode);

        if (resolvedPhase === "ended" && !isCancelling) {
          await handleCallEnded(callState);
          return;
        }

        if (statusCode === 100) {
          setIsMTLBenefitModalOpened(true);
        }

        if (location.pathname === "/call" && statusCode <= 6) {
          await checkQueueRef.current();
        }
      }

      if (resolvedPhase === "waiting") {
        deactivateAcsIframe();
      }

      if (
        resolvedPhase === "in_call" &&
        callState.patientJoinUrl &&
        typeof callState.patientJoinUrl === "string"
      ) {
        queueAcsJoinUrl(callState.patientJoinUrl);
      }

      if (callState.insuranceConsentRequested && !isConsentModalOpened) {
        await handleConsentRequest(callState);
      }

      if (callState.isAssignedToTyto && !isTytoModalDismissed && !isTytoModalOpened) {
        setIsTytoModalOpened(true);
      }
    },
    [
      deactivateAcsIframe,
      handleCallEnded,
      handleConsentRequest,
      isCancelling,
      isConsentModalOpened,
      isTytoModalDismissed,
      isTytoModalOpened,
      location.pathname,
      queueAcsJoinUrl,
      resolveCallStatePhase,
    ]
  );

  const extractCallState = useCallback((data: any): CallStateMessage | null => {
    if (!data) return null;
    if (data.type === "call_state") return data as CallStateMessage;

    const message = data.message;
    if (message && typeof message === "object" && message.type === "call_state") {
      return message as CallStateMessage;
    }

    if (message && typeof message === "string") {
      try {
        const parsed = JSON.parse(message);
        if (parsed?.type === "call_state") {
          return parsed as CallStateMessage;
        }
      } catch {
        return null;
      }
    }

    return null;
  }, []);

  checkQueueRef.current = async () => {
    if (location.pathname !== "/call") {
      console.log("[checkQueue] Not on call page - skipping check");
      return;
    }

    if (!isSessionCoverageActiveRef.current) {
      console.log("[checkQueue] Session coverage inactive - skipping check");
      return;
    }

    const result = await transactionService.checkQueue();
    const transactionCode = transactionService.getTransactionCode();
    if (transactionCode) {
      sessionTransactionCodeRef.current = transactionCode;
    }
    const status = transactionService.getTransactionStatus();
    setTransactionStatus(status);
    setIsCancelDisabled(transactionService.getTransactionProgress() > 2);

    const joinUrl = result?.PatientJoinUrl;
    if (joinUrl && typeof joinUrl === "string" && [2, 4, 6].includes(status)) {
      queueAcsJoinUrl(joinUrl);
    }

    if (result && status > 0) {
      if (status > 6 && status < 100) {
        if (isCancelling) return;
        finishSessionAndNavigateToReview();
        return;
      }

      if (status === 100) {
        setIsMTLBenefitModalOpened(true);
        return;
      }

      if ([1, 2].includes(status)) {
        setRecipientImage(result.NurseImage || assetService.getNurse());
        setRecipientName(result.NurseName);
        setRecipientSpecialty(result.NurseSpecialty);
      } else if ([3, 4].includes(status)) {
        setRecipientImage(result.DoctorImage || assetService.getDoctor());
        setRecipientName(result.DoctorName);
        setRecipientSpecialty(result.DoctorSpecialty);
      } else if ([5, 6].includes(status)) {
        setRecipientImage(result.PharmacyImage || assetService.getNurse());
        setRecipientName(result.PharmacyName);
        setRecipientSpecialty(result.PharmacySpecialty);
      }
    }
  };

  const onWebSocketMessage = useCallback(
    async (data: any) => {
      if (hasProcessedCallEnd.current || !isSessionCoverageActiveRef.current) {
        console.log("[WebSocket] Ignoring message after session completion");
        return;
      }

      console.log("[WebSocket] Received message:", data);

      if (
        data.type === "consent_request" ||
        (data.type === "user_message" && data.message === "consent_request")
      ) {
        await handleConsentRequest(data);
        return;
      }

      if (
        data.type === "consent_response" ||
        (data.type === "user_message" && data.message === "consent_response")
      ) {
        await handleConsentResponse(data);
        return;
      }

      if (
        data.type === "tyto_assignment" ||
        (data.type === "user_message" && data.message === "tyto_assignment")
      ) {
        await handleTytoAssignment(data);
        return;
      }

      if (
        data.type === "tyto_sms_status" ||
        (data.type === "user_message" && data.message === "tyto_sms_status")
      ) {
        await handleTytoSmsStatus(data);
        return;
      }

      if (
        data.type === "call_transferred" ||
        (data.type === "user_message" && data.message === "call_transferred")
      ) {
        await handleCallTransferred(data);
        return;
      }

      if (
        data.type === "call_ended" ||
        (data.type === "user_message" && data.message === "call_ended")
      ) {
        await handleCallEnded(data);
        return;
      }

      const callState = extractCallState(data);
      if (callState) {
        await handleCallState(callState);
        return;
      }

      console.log("[WebSocket] Ignoring unsupported websocket message shape");
    },
    [
      extractCallState,
      handleCallEnded,
      handleCallState,
      handleCallTransferred,
      handleConsentRequest,
      handleConsentResponse,
      handleTytoAssignment,
      handleTytoSmsStatus,
    ]
  );

  const scheduleReconnect = useCallback(() => {
    if (
      !isSessionCoverageActiveRef.current ||
      reconnectTimerRef.current ||
      isReconnectInFlightRef.current ||
      isWebSocketConnectedRef.current
    ) {
      return;
    }

    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      const transactionCode = sessionTransactionCodeRef.current;
      if (!transactionCode) {
        return;
      }

      runReconnectAttemptRef.current(transactionCode);
    }, RECONNECT_INTERVAL_MS);
  }, []);

  const runReconnectAttempt = useCallback(
    async (transactionCode: string) => {
      if (
        !isSessionCoverageActiveRef.current ||
        sessionTransactionCodeRef.current !== transactionCode ||
        isReconnectInFlightRef.current
      ) {
        return;
      }

      clearReconnectTimer();
      isReconnectInFlightRef.current = true;

      try {
        const businessUnit = sessionBusinessUnitRef.current ?? "1";
        await websocketService.connect(transactionCode, businessUnit);
      } catch (error) {
        console.error("[WebSocket] Reconnect attempt failed:", error);
      } finally {
        isReconnectInFlightRef.current = false;

        if (
          !isSessionCoverageActiveRef.current ||
          sessionTransactionCodeRef.current !== transactionCode
        ) {
          return;
        }

        try {
          // Retry coverage guarantees one poll after each reconnect attempt.
          await checkQueueRef.current();
        } catch (error) {
          console.error("[checkQueue] Retry poll failed:", error);
        }

        if (
          !isSessionCoverageActiveRef.current ||
          sessionTransactionCodeRef.current !== transactionCode
        ) {
          return;
        }

        if (!isWebSocketConnectedRef.current) {
          scheduleReconnect();
        }
      }
    },
    [clearReconnectTimer, scheduleReconnect]
  );

  runReconnectAttemptRef.current = (transactionCode: string) => {
    void runReconnectAttempt(transactionCode);
  };

  const onWebSocketOpen = useCallback(() => {
    clearReconnectTimer();
    setIsWebSocketConnected(true);
    isWebSocketConnectedRef.current = true;
  }, [clearReconnectTimer]);

  const onWebSocketClose = useCallback(() => {
    setIsWebSocketConnected(false);
    isWebSocketConnectedRef.current = false;
    if (location.pathname === "/call" && isSessionCoverageActiveRef.current) {
      scheduleReconnect();
    }
  }, [location.pathname, scheduleReconnect]);

  const onWebSocketError = useCallback(
    (error: any) => {
      console.error("[WebSocket] Error occurred:", error);
      setIsWebSocketConnected(false);
      isWebSocketConnectedRef.current = false;
      if (location.pathname === "/call" && isSessionCoverageActiveRef.current) {
        scheduleReconnect();
      }
    },
    [location.pathname, scheduleReconnect]
  );

  // Keep websocket handlers bound to latest React closures.
  useEffect(() => {
    websocketService.off("open", onWebSocketOpen);
    websocketService.off("message", onWebSocketMessage);
    websocketService.off("close", onWebSocketClose);
    websocketService.off("error", onWebSocketError);

    websocketService.on("open", onWebSocketOpen);
    websocketService.on("message", onWebSocketMessage);
    websocketService.on("close", onWebSocketClose);
    websocketService.on("error", onWebSocketError);

    return () => {
      websocketService.off("open", onWebSocketOpen);
      websocketService.off("message", onWebSocketMessage);
      websocketService.off("close", onWebSocketClose);
      websocketService.off("error", onWebSocketError);
    };
  }, [onWebSocketClose, onWebSocketError, onWebSocketMessage, onWebSocketOpen]);

  const bootstrapQueueSession = useCallback(
    async (transactionCode: string, businessUnit: string) => {
      if (!transactionCode) {
        return;
      }

      resetQueueUiState();
      startSessionCoverage(transactionCode, businessUnit);
      sessionStorage.setItem("transactionCode", transactionCode);

      try {
        await websocketService.connect(transactionCode, businessUnit);
      } catch (error) {
        console.error("[WebSocket] Initial connection failed:", error);
        scheduleReconnect();
      }

      setTimeout(() => {
        checkQueueRef.current();
      }, 500);
    },
    [resetQueueUiState, scheduleReconnect, startSessionCoverage]
  );

  const disconnectWebSocket = useCallback(() => {
    resetActiveWebSocketConnection();
    websocketService.off("open", onWebSocketOpen);
    websocketService.off("message", onWebSocketMessage);
    websocketService.off("close", onWebSocketClose);
    websocketService.off("error", onWebSocketError);
    websocketService.clearEventHandlers();
  }, [
    onWebSocketClose,
    onWebSocketError,
    onWebSocketMessage,
    onWebSocketOpen,
    resetActiveWebSocketConnection,
  ]);

  const createQueue = useCallback(async () => {
    if (hasCreatedQueue.current) return;
    hasCreatedQueue.current = true;
    if (!partner) return;

    let source = "";
    let partnerConsultId = "";

    if (partnerAliveService.isActive()) {
      partnerConsultId = (partner as ALive).aliveConsultId;
    }

    if (partnerInterpreterService.isActive()) {
      source = `Interpreter-${(
        partner as Interpreter
      ).interpreterLanguage.toUpperCase()}`;
      partner.hospitalId = await systemService.getHospitalId("Interpreter");
      partnerService.setPartner(partner);
    }

    if (partner?.name) {
      const partnerName = partner.name.toLowerCase().trim();
      if (["sth", "ssh", "sch", "sct", "snh er"].includes(partnerName)) {
        let hospitalName = partnerName.toUpperCase();
        if (partnerName === "snh er") hospitalName = "SNH";
        partner.hospitalId = await systemService.getHospitalId(hospitalName);
        partnerService.setPartner(partner);
      }
    }

    try {
      const queueResult = await transactionService.createQueue(
        partner,
        source,
        partnerConsultId
      );

      if (queueResult.isExistingTransaction) {
        await navigate("/review");
        return;
      }

      if (queueResult.transactionCode) {
        await bootstrapQueueSession(
          queueResult.transactionCode,
          partner?.hospitalId?.toString() ?? "1"
        );
        await loggingService(
          "press_call",
          {
            message: "[CreateQueue] Queue created successfully",
            data: queueResult,
          },
          "[CreateQueue] Logging press_call failed"
        );
      }
    } catch (error) {
      console.error("createQueue failed", error);
    }
  }, [bootstrapQueueSession, navigate, partner]);

  const initPartnerUi = useCallback(async () => {
    const transactionProgress = transactionService.getTransactionProgress();
    if (!partner || (transactionProgress > 6 && transactionProgress < 100)) {
      navigate("/");
      return;
    }

    setLogo(assetService.getLogo(partner.name));
    if (partnerSatService.isActive()) {
      setLoading(assetService.getLoading(partnerSatService.onWaitingGetLoading()));
    } else {
      setLoading(assetService.getLoading(partner.name));
    }
    await createQueue();
  }, [createQueue, navigate, partner]);

  const consentModalClosed = () => setIsConsentModalOpened(false);
  const tytoModalClosed = () => {
    setIsTytoModalOpened(false);
    setIsTytoModalDismissed(true);
  };

  const handleCancelCallConfirm = async () => {
    await loggingService(
      "press_callback",
      { message: "[CancelCall] Cancel call confirmed" },
      "[CancelCall] Logging press_callback failed"
    );

    if (partnerSatService.isActive() && (partner as any)?.serviceOption === "1") {
      if (partner) {
        const replacementPartner = {
          ...partner,
          hospitalId: 0,
        };

        stopSessionCoverage();
        resetActiveWebSocketConnection();
        resetQueueUiState();
        partnerService.setPartner(replacementPartner);

        const queueResult = await transactionService.createQueue(
          replacementPartner,
          "",
          ""
        );

        if (queueResult.isExistingTransaction) {
          isIntentionalLeave.current = true;
          await navigate("/review");
          return;
        }

        if (queueResult.transactionCode) {
          await bootstrapQueueSession(
            queueResult.transactionCode,
            replacementPartner.hospitalId?.toString() ?? "1"
          );
        }
      }
      return;
    }

    setIsCancelling(true);
    stopSessionCoverage();
    await transactionService.cancelQueue();
    localStorage.setItem("callEnded", "true");
    isIntentionalLeave.current = true;

    const currentLang = i18n.language;
    i18n.changeLanguage(currentLang).then(() => {
      navigate("/callback");
    });
  };

  useEffect(() => {
    const ua = navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);

    const pushCallState = () => {
      window.history.pushState({ page: "call" }, "", window.location.href);
    };

    let popstateHandler: (() => void) | null = null;
    let visibilityHandler: (() => void) | null = null;

    if (isIOS && isSafari) {
      pushCallState();
      pushCallState();
      pushCallState();

      popstateHandler = () => {
        pushCallState();
      };
      window.addEventListener("popstate", popstateHandler);

      visibilityHandler = () => {
        if (!document.hidden) {
          pushCallState();
          pushCallState();
        }
      };
      document.addEventListener("visibilitychange", visibilityHandler);
    } else {
      window.history.pushState(null, "", window.location.href);
    }

    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      if (isIntentionalLeave.current) return;
      event.preventDefault();
      event.returnValue =
        "Are you sure you want to leave the call? This will end your current session.";
      return event.returnValue;
    };
    window.addEventListener("beforeunload", beforeUnloadHandler);

    return () => {
      if (popstateHandler) {
        window.removeEventListener("popstate", popstateHandler);
      }
      if (visibilityHandler) {
        document.removeEventListener("visibilitychange", visibilityHandler);
      }
      window.removeEventListener("beforeunload", beforeUnloadHandler);
    };
  }, []);

  useEffect(() => {
    initPartnerUi();
    return () => {
      stopSessionCoverage();
      disconnectWebSocket();
      if (callStateJoinUrlTimer.current) {
        clearTimeout(callStateJoinUrlTimer.current);
        callStateJoinUrlTimer.current = null;
      }
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        isIntentionalLeave.current = true;
      }
    };
    // Legacy behavior: run exactly once on mount/unmount.
    // Re-running this effect tears down websocket listeners during call flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAcsIframeActive) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }
  }, [isAcsIframeActive]);

  const showLogo = !partnerSatService.isActive();
  const isWaitingOverlayActive = !!acsJoinUrl && !isAcsIframeActive;

  return {
    showLogo,
    isWaitingOverlayActive,
    logo,
    loading,
    recipientImage,
    recipientName,
    recipientSpecialty,
    transactionStatus,
    isCancelDisabled,
    acsJoinUrl,
    isAcsIframeActive,
    consentModalClosed,
    isConsentModalOpened,
    isTytoModalOpened,
    tytoModalClosed,
    isMTLBenefitModalOpened,
    setIsMTLBenefitModalOpened,
    checkQueueRef,
    handleCancelCallConfirm,
  };
}
